import { cn } from '@/lib/utils';
import Markdown from '@/components/markdown/Markdown';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import type { RespondentMap } from './useRespondentMap';

/**
 * Match `[R12]` / `[Q<uuid>]` / `[T3]` citation tokens. Same pattern
 * the chat bubble uses so both surfaces tokenise identically. The
 * `g` flag is required for `.split(...)`; `i` is for `[Q...]` UUIDs.
 */
const CITATION_PATTERN = /(\[R\d+\])|(\[Q[a-z0-9-]+\])|(\[T\d+\])/gi;

/**
 * TELV1-296 follow-up — markdown structural-marker leak in chat bubbles.
 *
 * The inline variant collapses `\n{2,}` to single spaces so the bubble's
 * single-run layout contract holds. The side effect is that any
 * block-level Markdown the LLM emits into a CHAT reply (`## headings`,
 * `---` hrs, `>` blockquotes, `| ... |` tables, `-` / `1.` lists) never
 * gets a leading blank line, so the underlying `<Markdown>` component
 * — already running with `isParagraphSpanEl`, where lists become spans
 * anyway — renders those markers as literal text. Result for the user:
 * `## 1. Theme name`, `---`, `> Important caveat:` show up as raw
 * characters in the bubble.
 *
 * Per system-architect verdict: this lives in CitationPillText, not in
 * the SSE consumer, because the rendering contract ("inline variant ⇒
 * no block-level structure allowed") is local to this component. The
 * SSE stream is reused by the artifact panel where blocks ARE wanted,
 * so normalising upstream would corrupt that surface.
 *
 * The flattening is intentionally lossy-but-readable rather than
 * structure-preserving: headings retain their text as `**bold**` so
 * emphasis hierarchy survives, hrs / blockquote markers / pipe rows /
 * leading bullet markers are dropped, list items keep their content as
 * a sentence run. Block-fenced code is left intact (rare in chat;
 * inline backticks already work).
 *
 * The complementary backend defence-in-depth is a prompt-level
 * "CHAT-ONLY SYNTAX BAN" added to `research-assistant.context.ts`'s
 * OUTPUT FORMAT section — but model behaviour drifts across versions,
 * so the renderer guard is the load-bearing fix.
 */
function flattenBlockMarkdown(text: string): string {
  return (
    text
      // Markdown headings (#, ##, ###, …): keep the text, wrap in bold so
      // the emphasis the LLM intended survives even though we strip the
      // heading level. Match at line-start OR after the first newline.
      .replace(
        /(^|\n)#{1,6}\s+([^\n]+)/g,
        (_, prefix: string, body: string) => `${prefix}**${body.trim()}**`,
      )
      // Horizontal rules (---, ***, ___ on their own line): drop. Be
      // strict to avoid eating em-dashes mid-sentence.
      .replace(/(^|\n)\s*(?:-{3,}|\*{3,}|_{3,})\s*(?=\n|$)/g, '$1')
      // Blockquote markers at line-start: drop the `> ` prefix, keep the
      // quoted text. Repeat to handle nested `>> `.
      .replace(/(^|\n)>+\s?/g, '$1')
      // Markdown table rows (`| cell | cell |`) and separator rows
      // (`|---|---|`): drop the whole line — preserving structure
      // inline would be unreadable, and the LLM should be putting
      // tables in a data_table or markdown_report artifact.
      .replace(/(^|\n)\s*\|[^\n]*\|[ \t]*(?=\n|$)/g, '$1')
      // Unordered list markers at line-start (- item, * item, + item):
      // drop the marker, keep the content. Avoid matching `**bold**`
      // (no space after the asterisk) or em-dash `—`.
      .replace(/(^|\n)[ \t]*[-*+][ \t]+/g, '$1')
      // Ordered list markers (1. item, 2) item): drop the marker.
      .replace(/(^|\n)[ \t]*\d+[.)][ \t]+/g, '$1')
  );
}

interface CitationPillTextProps {
  /**
   * The text to render. Will be tokenised on `[Rn]` / `[Qx]` / `[Tn]`
   * and rendered as a mix of inline markdown + clickable pills.
   */
  text: string;
  /**
   * Lookup for `[Rn]` pills — when present and the respondent has a
   * conversation_id, the pill becomes clickable and opens the
   * transcript panel. Missing entries render as a disabled pill.
   */
  respondentMap: RespondentMap;
  /**
   * Layout hint:
   *   - `'paragraph'` (default) splits on blank-line paragraph
   *     breaks first, then handles citations INLINE within each
   *     paragraph so punctuation around a pill stays inline with the
   *     surrounding text.
   *   - `'inline'` renders the whole thing inline-only (used by chat
   *     bubbles where the parent is already a `<span>`); paragraph
   *     breaks collapse to spaces.
   */
  variant?: 'paragraph' | 'inline';
  /** Optional className applied to the outer wrapper. */
  className?: string;
}

/**
 * D.3 + theme/markdown artifact pill rendering — shared across the
 * chat bubble (`MessageBubble`) and the two artifact renderers that
 * carry `[Rn]` tokens in their body content (`ArtifactThemeSummary`
 * for theme bodies, `ArtifactMarkdownReport` for free-form report
 * content).
 *
 * Click behaviour by token type:
 *   - `[Rn]` → opens the respondent's transcript panel (when their
 *             conversation_id is known via `respondentMap`); inert
 *             grey pill otherwise.
 *   - `[Q<uuid>]` → hash-anchor `#question-<id>` for a future
 *                   Results-page handler; styled green pill in the
 *                   meantime so the token isn't raw text.
 *   - `[T<n>]` → inert violet pill (no navigation target yet —
 *               theme IDs are positional).
 *
 * TELV1-296 #2c — previous implementation split the whole text on
 * citation tokens and rendered EACH chunk through Markdown
 * independently. Markdown then wrapped each chunk in its own `<p>`,
 * which produced a stray full-stop / comma as a paragraph any time
 * the LLM placed a citation mid-sentence:
 *
 *     "The result was great [R12]. Next finding..."
 *     →  <p>The result was great </p>
 *        <button>R12</button>
 *        <p>. Next finding...</p>   ← orphan period
 *
 * The QA report ("paragraph starts with a comma or a full stop stands
 * in the middle of the report") was driven by exactly this. The new
 * structure splits on blank-line paragraph breaks first, then within
 * each paragraph renders the citations inline as text-flow siblings
 * — preserving paragraph boundaries AND keeping punctuation attached
 * to its sentence.
 */
function CitationPillText({
  text,
  respondentMap,
  variant = 'paragraph',
  className,
}: CitationPillTextProps) {
  const { open: openTranscriptPanel } = useTranscriptPanelStore();

  if (!text) return null;

  // TELV1-296 follow-up — strip a stray leading `: ` / `:` at the very
  // start of the response. Observed in browser-test Q1 MiniMe: response
  // body literally started with `: parents care most about…`. Root
  // cause is the LLM emitting `**Summary**: …` (or similar label) where
  // the bold prefix tokens get eaten by the streaming markdown renderer
  // mid-flight, leaving a dangling colon. The complementary backend
  // prompt addition forbids `**Label:** ` prefixes; this is the
  // defence-in-depth renderer guard.
  const normalisedText = text.replace(/^[ \t]*:\s+/, '');

  // Render a single citation token as the appropriate pill, or `null`
  // if the token didn't match any of the three patterns.
  const renderPill = (part: string, key: string | number) => {
    const rMatch = part.match(/^\[R(\d+)\]$/);
    if (rMatch) {
      const respondentIdx = parseInt(rMatch[1], 10);
      const ref = respondentMap.get(respondentIdx);
      const isClickable = !!ref;
      return (
        <button
          key={key}
          type="button"
          disabled={!isClickable}
          aria-disabled={!isClickable}
          className={cn(
            'inline-flex items-center justify-center min-w-5 h-5 px-1.5 mx-0.5 font-semibold text-[11px] text-white rounded-full transition-opacity align-text-top focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1',
            isClickable
              ? 'bg-primary opacity-90 hover:opacity-100 cursor-pointer'
              : 'bg-neutral-300 opacity-60 cursor-not-allowed',
          )}
          aria-label={
            isClickable
              ? `View transcript for Respondent ${rMatch[1]}`
              : `Respondent ${rMatch[1]} (transcript not available in this view)`
          }
          title={
            isClickable ? `View Respondent ${rMatch[1]}'s transcript` : `Respondent ${rMatch[1]}`
          }
          onClick={() => {
            if (ref) openTranscriptPanel(ref.conversationId, ref.messageId, ref.questionId);
          }}
        >
          {rMatch[1]}
        </button>
      );
    }
    const qMatch = part.match(/^\[Q([a-z0-9-]+)\]$/i);
    if (qMatch) {
      const questionId = qMatch[1];
      return (
        <a
          key={key}
          href={`#question-${questionId}`}
          aria-label={`Jump to question ${questionId}`}
          title={`Question ${questionId}`}
          className="inline-flex items-center justify-center h-5 px-1.5 mx-0.5 font-semibold text-[11px] text-white rounded-full bg-emerald-600 hover:bg-emerald-700 transition-colors align-text-top focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-1 no-underline"
        >
          Q·{questionId.slice(0, 4)}
        </a>
      );
    }
    const tMatch = part.match(/^\[T(\d+)\]$/);
    if (tMatch) {
      return (
        <span
          key={key}
          aria-label={`Theme ${tMatch[1]}`}
          title={`Theme ${tMatch[1]}`}
          className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 mx-0.5 font-semibold text-[11px] text-white rounded-full bg-violet-600 align-text-top"
        >
          T{tMatch[1]}
        </span>
      );
    }
    return null;
  };

  // Tokenise ONE paragraph: split on citation tokens, render each
  // text chunk through inline Markdown and each citation through a
  // pill. Returns an array of React nodes that all live inside the
  // SAME paragraph wrapper, so punctuation immediately after a pill
  // (the QA report's "stray full stop") stays glued to its sentence.
  //
  // Keys note (claude-initial-review #1): the children are keyed by
  // `${paragraphKey}-{pill|text}-${i}` where `i` is the index in the
  // citation-split output. Indices are safe here because:
  //   • the artifact `text` content is immutable for the lifetime of
  //     a rendered report (LLM-produced, never mutated client-side),
  //   • `CITATION_PATTERN.split(text)` is a pure function of `text`,
  //     so the positional order is stable across re-renders,
  //   • paragraphs are scoped by the outer `paragraphKey` so children
  //     never collide across paragraphs.
  // No reordering, no mid-list insertion, no semantic identity per
  // chunk — so the React reconciliation risk of array-index keys
  // doesn't apply.
  const renderParagraphInline = (paragraph: string, paragraphKey: number) => {
    const parts = paragraph.split(CITATION_PATTERN).filter((p) => p !== undefined);
    // TELV1-296 follow-up — track whether the previous emitted sibling
    // was a citation pill so we can strip stray `[Rn]:` ref-link residue.
    //
    // LLMs occasionally emit Markdown reference-link syntax — e.g.
    // `[R5]: "Ik wil graag meer verdienen"` — instead of the inline
    // citation pattern we want. The regex pulls the `[R5]` token into a
    // pill, leaving the following text chunk starting with `: `. The
    // user then sees a stray colon adrift between the pill and the
    // quote ("[5] : \"…\"").
    //
    // Per system-architect verdict: post-strip in the renderer, not
    // tighter regex — we don't want to conflate the citation grammar
    // with one specific failure mode, since `: ` is legitimate elsewhere.
    // Only strip when the immediately-preceding node was a pill.
    let prevWasPill = false;
    return parts.map((part, i) => {
      if (!part) return null;
      const pill = renderPill(part, `${paragraphKey}-pill-${i}`);
      if (pill) {
        prevWasPill = true;
        return pill;
      }
      // Inline markdown chunk — render via `isParagraphSpanEl` so the
      // chunk's Markdown output is a `<span>`, not a nested `<p>`,
      // letting us mix it with sibling pills inside one paragraph.
      const trimmed = prevWasPill ? part.replace(/^\s*:\s+/, ' ') : part;
      prevWasPill = false;
      if (!trimmed) return null;
      return (
        <Markdown
          // eslint-disable-next-line react/no-array-index-key
          key={`${paragraphKey}-text-${i}`}
          isParagraphSpanEl
        >
          {trimmed}
        </Markdown>
      );
    });
  };

  // Inline variant (chat bubbles): collapse everything to inline
  // tokens, no `<p>` wrappers. Paragraph breaks become spaces so the
  // surrounding inline-context parent doesn't get unexpected blocks.
  //
  // BEFORE collapsing paragraphs we flatten block-level markdown so
  // headings / hrs / blockquotes / tables / lists don't end up as
  // literal `## 1. Theme name`, `---`, `> caveat:` characters in the
  // bubble. See `flattenBlockMarkdown`'s docblock for full rationale.
  if (variant === 'inline') {
    const flat = flattenBlockMarkdown(normalisedText).replace(/\n{2,}/g, ' ');
    return (
      <span className={cn('leading-relaxed', className)}>{renderParagraphInline(flat, 0)}</span>
    );
  }

  // Paragraph variant (artifact bodies, theme summaries, report
  // content): split on blank-line breaks FIRST, then render each
  // paragraph as a `<p>` with citations inline. Empty paragraphs (e.g.
  // from triple-newlines or trailing newlines) are skipped so we don't
  // emit empty `<p>` blocks.
  const paragraphs = normalisedText.split(/\n{2,}/);
  return (
    <div className={className}>
      {paragraphs.map((paragraph, idx) => {
        const trimmed = paragraph.trim();
        if (!trimmed) return null;
        return (
          <p
            // eslint-disable-next-line react/no-array-index-key
            key={`p-${idx}`}
            className="mb-5 last:mb-0 leading-relaxed"
          >
            {renderParagraphInline(trimmed, idx)}
          </p>
        );
      })}
    </div>
  );
}

export default CitationPillText;
